import { createSlice } from '@reduxjs/toolkit'

import { logoutUser } from '../actions/userActions'
import { createLoadingReducer } from './shared'
import { AdCreativeWithUrls } from 'common/api/v1/types'
import {
  clearAdCreative,
  deleteAdCreative,
  deleteAdCreatives,
  getAdCreative,
  listAdCreatives,
  updateAdCreative,
} from '../actions/adCreativeActions'
import type { ApplicationException } from '../../api/ApplicationException'

interface State {
  adCreatives: Array<AdCreativeWithUrls>
  selectedAdCreative?: AdCreativeWithUrls
  total: number
  loading: boolean
  saving: boolean
  formErrors?: ApplicationException['errorInfo']['details']
}

export const initialAdCreatives: State = {
  adCreatives: [],
  selectedAdCreative: undefined,
  total: 0,
  loading: false,
  saving: false,
  formErrors: undefined,
}

const { isLoadingAction, loadingReducer } = createLoadingReducer<State>(listAdCreatives)

const adCreativeSlice = createSlice({
  name: 'adCreative',
  initialState: initialAdCreatives,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(
        getAdCreative.fulfilled,
        (state, { payload: selectedAdCreative }): State => ({ ...state, selectedAdCreative }),
      )
      .addCase(clearAdCreative, (state): State => ({ ...state, selectedAdCreative: undefined }))

      .addCase(updateAdCreative.pending, (state): State => ({ ...state, saving: true }))
      .addCase(updateAdCreative.rejected, (state): State => ({ ...state, saving: false }))
      .addCase(
        updateAdCreative.fulfilled,
        (state, { payload: adCreative }): State => ({
          ...state,
          adCreatives: [adCreative, ...state.adCreatives.filter((o) => o.id !== adCreative.id)],
          saving: false,
        }),
      )
      .addCase(deleteAdCreative.pending, (state): State => ({ ...state, saving: true }))
      .addCase(deleteAdCreative.rejected, (state): State => ({ ...state, saving: false }))
      .addCase(
        deleteAdCreative.fulfilled,
        (state, { payload: e }): State => ({
          ...state,
          adCreatives: state.adCreatives.filter((o) => o.id !== e.id),
          saving: false,
        }),
      )
      .addCase(
        deleteAdCreatives.fulfilled,
        (state, { payload: ids }): State => ({
          ...state,
          adCreatives: state.adCreatives.filter((o) => !ids.includes(o.id)),
          saving: false,
        }),
      )
      .addCase(
        listAdCreatives.fulfilled,
        (state, { payload: adCreatives }): State => ({
          ...state,
          adCreatives: adCreatives.items,
          total: adCreatives.total,
          loading: false,
        }),
      )
      .addCase(logoutUser.fulfilled, (): State => initialAdCreatives)
      .addMatcher(isLoadingAction, loadingReducer)
  },
})

export default adCreativeSlice.reducer
